import { useState, useMemo, useCallback } from 'react';
import { AlertTriangle, CheckCircle, XCircle, Link2, Loader2, Star } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { SeverityBadge } from '../ui/SeverityBadge';
import { cn } from '../../lib/utils';
import type { EnrichedIncident, DefenderClassification, DefenderDetermination } from '../../data/types';
import {
  getClassificationOptions,
  getDeterminationOptions,
  getClassificationLabel,
  getDeterminationLabel,
} from '../../services/defender';
import { submitIncidentFeedback, updateIncidentStatus } from '../../services/incidents';

interface BulkCloseModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedIncidents: EnrichedIncident[];
  onComplete: () => void; // Called when bulk operation completes to refresh data
}

export interface BulkCloseParams {
  incidents: Array<{ tenantId: string; incidentId: string }>;
  classification: DefenderClassification;
  determination: DefenderDetermination;
  closingNote: string;
  linkIncidents: boolean;
  linkGroupName?: string;
}

type ProcessingState = 'idle' | 'processing' | 'complete';

interface ProcessingResult {
  tenantId: string;
  incidentId: string;
  title: string;
  success: boolean;
  defenderSyncFailed?: boolean;
  feedbackFailed?: boolean;
  error?: string;
}

/** Matches MIN_REASON_LENGTH in func-axn-analyst-api/src/lib/closureGuard.ts. */
const MIN_NOTE_LENGTH = 20;
const ABOVE_MEDIUM = ['high', 'critical'];

export function BulkCloseModal({
  isOpen,
  onClose,
  selectedIncidents,
  onComplete,
}: BulkCloseModalProps) {
  // Form state
  // No pre-selection (see StatusManager): a batch close must say what it is.
  const [classification, setClassification] = useState<DefenderClassification | ''>('');
  const [determination, setDetermination] = useState<DefenderDetermination | ''>('');
  const [closingNote, setClosingNote] = useState('');
  const [linkIncidents, setLinkIncidents] = useState(false);
  const [linkGroupName, setLinkGroupName] = useState('');
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackComments, setFeedbackComments] = useState('');

  // Processing state
  const [processingState, setProcessingState] = useState<ProcessingState>('idle');
  const [results, setResults] = useState<ProcessingResult[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const classificationOptions = getClassificationOptions();
  const determinationOptions = classification ? getDeterminationOptions(classification) : [];

  // Summary stats
  const stats = useMemo(() => {
    const bySeverity: Record<string, number> = {};
    const byTenant: Record<string, number> = {};

    selectedIncidents.forEach((inc) => {
      bySeverity[inc.severity] = (bySeverity[inc.severity] || 0) + 1;
      const tenantName = inc.tenantDisplayName || inc.tenantId;
      byTenant[tenantName] = (byTenant[tenantName] || 0) + 1;
    });

    return { bySeverity, byTenant };
  }, [selectedIncidents]);

  const hasCritical = (stats.bySeverity['critical'] || 0) > 0;

  // Closing anything above medium severity needs a written comment (enforced
  // by the analyst-api closure guard). The old auto-generated "Bulk closed - …"
  // text satisfied that rule without anyone writing a word, so it is no longer
  // sent for those incidents: the analyst writes one note for the batch.
  const aboveMediumCount = (stats.bySeverity['high'] || 0) + (stats.bySeverity['critical'] || 0);
  const noteRequired = aboveMediumCount > 0;
  const noteCharsNeeded = Math.max(0, MIN_NOTE_LENGTH - closingNote.trim().length);
  const noteMissing = noteRequired && noteCharsNeeded > 0;
  const classificationMissing = classification === '' || determination === '';

  // Process a single incident
  const closeIncident = useCallback(
    async (incident: EnrichedIncident): Promise<ProcessingResult> => {
      if (classification === '' || determination === '') {
        return { tenantId: incident.tenantId, incidentId: incident.incidentId, title: incident.title, success: false, error: 'Choose a classification and determination first' };
      }
      try {
        // Step 1: Update platform status to closed
        const statusResult = await updateIncidentStatus(
          incident.tenantId,
          incident.incidentId,
          'closed',
          // A written note is always sent as written. Without one, the stock
          // text is used only for medium and below — never as a stand-in for
          // the comment a high-severity closure requires.
          closingNote.trim()
            || (ABOVE_MEDIUM.includes((incident.severity ?? '').toLowerCase())
              ? undefined
              : `Bulk closed - ${getClassificationLabel(classification)}: ${getDeterminationLabel(determination)}`),
          classification,
          determination
        );

        if (!statusResult.success) {
          return {
            tenantId: incident.tenantId,
            incidentId: incident.incidentId,
            title: incident.title,
            success: false,
            error: statusResult.error || 'Failed to update status',
          };
        }

        // updateIncidentStatus already performs Defender write-back server-side.
        // Submit the optional shared rating once for each successfully closed
        // incident so bulk handling contributes to the same accuracy dataset.
        let feedbackFailed = false;
        if (feedbackRating > 0) {
          const feedbackResult = await submitIncidentFeedback(
            incident.tenantId,
            incident.incidentId,
            {
              feedbackType: 'overall',
              rating: feedbackRating,
              comments: feedbackComments.trim() || undefined,
              source: 'closure',
              idempotencyKey: crypto.randomUUID(),
            },
          );
          feedbackFailed = !feedbackResult.success;
        }

        // Platform status updated successfully - that's a success even if Defender sync failed
        return {
          tenantId: incident.tenantId,
          incidentId: incident.incidentId,
          title: incident.title,
          success: true,
          defenderSyncFailed: Boolean(
            statusResult.defenderSync?.attempted && !statusResult.defenderSync.succeeded,
          ),
          feedbackFailed,
        };
      } catch (error) {
        return {
          tenantId: incident.tenantId,
          incidentId: incident.incidentId,
          title: incident.title,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    [classification, determination, closingNote, feedbackRating, feedbackComments]
  );

  const handleConfirm = async () => {
    setProcessingState('processing');
    setCurrentIndex(0);
    setResults([]);

    const allResults: ProcessingResult[] = [];

    // Process incidents one by one with visual feedback
    for (let i = 0; i < selectedIncidents.length; i++) {
      setCurrentIndex(i);
      const incident = selectedIncidents[i];
      const result = await closeIncident(incident);
      allResults.push(result);
      setResults([...allResults]);
    }

    setCurrentIndex(selectedIncidents.length);
    setProcessingState('complete');

    // Notify parent to refresh data if any succeeded
    if (allResults.some((r) => r.success)) {
      onComplete();
    }
  };

  const handleClose = () => {
    if (processingState === 'processing') return;
    
    // Reset state
    setProcessingState('idle');
    setResults([]);
    setCurrentIndex(0);
    setClosingNote('');
    setLinkIncidents(false);
    setLinkGroupName('');
    setFeedbackRating(0);
    setFeedbackComments('');
    
    onClose();
  };

  const successCount = results.filter((r) => r.success).length;
  const failureCount = results.filter((r) => !r.success).length;
  const defenderSyncFailedCount = results.filter((r) => r.success && r.defenderSyncFailed).length;
  const feedbackFailedCount = results.filter((r) => r.success && r.feedbackFailed).length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        processingState === 'complete'
          ? 'Bulk Close Complete'
          : `Close ${selectedIncidents.length} Incident${selectedIncidents.length !== 1 ? 's' : ''}`
      }
      description={
        processingState === 'idle'
          ? 'Configure how these incidents should be closed and classified'
          : undefined
      }
      size="lg"
      footer={
        processingState === 'complete' ? (
          <div className="flex justify-end">
            <Button onClick={handleClose}>Done</Button>
          </div>
        ) : (
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <AlertTriangle className="w-5 h-5" />
              <span className="text-sm font-medium">
                This will update {Object.keys(stats.byTenant).length} customer Defender tenant
                {Object.keys(stats.byTenant).length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex gap-3">
              <Button variant="ghost" onClick={handleClose} disabled={processingState === 'processing'}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={processingState === 'processing' || noteMissing || classificationMissing}
                className="bg-green-600 hover:bg-green-700"
              >
                {processingState === 'processing' ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Closing...
                  </>
                ) : (
                  'Close All Incidents'
                )}
              </Button>
            </div>
          </div>
        )
      }
    >
      {processingState === 'idle' && (
        <div className="space-y-6">
          {/* Warning for critical incidents */}
          {hasCritical && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-800 dark:text-red-200">
                  <strong>Warning:</strong> This selection includes{' '}
                  {stats.bySeverity['critical']} critical incident
                  {stats.bySeverity['critical'] !== 1 ? 's' : ''}. Please ensure these have been
                  fully investigated before closing.
                </div>
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="bg-bg-tertiary/50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-text-secondary mb-3">
              Selection Summary
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-text-secondary uppercase tracking-wide">
                  By Severity
                </span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(stats.bySeverity).map(([severity, count]) => (
                    <span
                      key={severity}
                      className="flex items-center gap-1.5 text-sm text-text-secondary"
                    >
                      <SeverityBadge severity={severity as EnrichedIncident['severity']} />
                      <span className="font-medium">{count}</span>
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-xs text-text-secondary uppercase tracking-wide">
                  By Tenant
                </span>
                <div className="mt-2 space-y-1">
                  {Object.entries(stats.byTenant).map(([tenant, count]) => (
                    <div
                      key={tenant}
                      className="flex justify-between text-sm text-text-secondary"
                    >
                      <span className="truncate">{tenant}</span>
                      <span className="font-medium">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Classification */}
          <div>
            <label htmlFor="bulk-classification" className="block text-sm font-medium text-text-secondary mb-2">
              Classification
            </label>
            <select
              id="bulk-classification"
              title="Select classification"
              value={classification}
              onChange={(e) => {
                setClassification(e.target.value as DefenderClassification | '');
                setDetermination('');
              }}
              className="w-full px-3 py-2 bg-bg-tertiary border border-border-default rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-blue"
            >
              <option value="">Choose how these incidents are classified…</option>
              {classificationOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Determination */}
          <div>
            <label htmlFor="bulk-determination" className="block text-sm font-medium text-text-secondary mb-2">
              Determination
            </label>
            <select
              id="bulk-determination"
              title="Select determination"
              value={determination}
              disabled={classification === ''}
              onChange={(e) => setDetermination(e.target.value as DefenderDetermination | '')}
              className="w-full px-3 py-2 bg-bg-tertiary border border-border-default rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-blue disabled:opacity-60"
            >
              <option value="">{classification ? 'Choose a determination…' : 'Choose a classification first'}</option>
              {determinationOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Closing Note */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Closing Note (will be added to all incidents){noteRequired ? ' — required' : ''}
            </label>
            <textarea
              value={closingNote}
              onChange={(e) => setClosingNote(e.target.value)}
              placeholder={noteRequired
                ? 'What did you check across these incidents, and what did it show?'
                : 'Add a note explaining why these incidents are being closed...'}
              rows={3}
              className="w-full px-3 py-2 bg-bg-tertiary border border-border-default rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-brand-blue resize-none"
            />
            {noteRequired && (
              <p className="mt-1 text-xs text-text-secondary">
                {aboveMediumCount} of the {selectedIncidents.length} selected incident{selectedIncidents.length === 1 ? ' is' : 's are'} high severity,
                so a closing note is needed. It is recorded against each one and is what the customer and reviewers will read.
                {noteCharsNeeded > 0 && <span className="text-amber-700 dark:text-amber-400"> {noteCharsNeeded} more character{noteCharsNeeded === 1 ? '' : 's'} needed.</span>}
              </p>
            )}
          </div>

          {/* Optional shared AI rating */}
          <div className="border-t border-border-default pt-4">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-text-secondary">
                Rate the AI analyses (optional)
              </label>
              {feedbackRating > 0 && <span className="text-xs text-text-secondary">Applied to each incident</span>}
            </div>
            <div className="mt-2 flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button key={star} type="button" onClick={() => setFeedbackRating(star)} aria-label={`${star} stars`}>
                  <Star className={cn('w-6 h-6', star <= feedbackRating ? 'text-amber-400 fill-amber-400' : 'text-text-muted')} />
                </button>
              ))}
            </div>
            {feedbackRating > 0 && (
              <textarea
                value={feedbackComments}
                onChange={(event) => setFeedbackComments(event.target.value)}
                placeholder="Shared accuracy feedback for this batch (optional)"
                rows={2}
                className="mt-2 w-full px-3 py-2 bg-bg-tertiary border border-border-default rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-brand-blue resize-none"
              />
            )}
          </div>

          {/* Link Incidents */}
          <div className="border-t border-border-default pt-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={linkIncidents}
                onChange={(e) => setLinkIncidents(e.target.checked)}
                className="mt-1 rounded border-border-default text-brand-blue focus:ring-brand-blue"
              />
              <div>
                <span className="flex items-center gap-2 text-sm font-medium text-text-secondary">
                  <Link2 className="w-4 h-4" />
                  Link these incidents together
                </span>
                <p className="text-xs text-text-secondary mt-1">
                  Create a relationship between these incidents for tracking purposes
                </p>
              </div>
            </label>

            {linkIncidents && (
              <div className="mt-3 ml-6">
                <input
                  type="text"
                  value={linkGroupName}
                  onChange={(e) => setLinkGroupName(e.target.value)}
                  placeholder="Enter a group name (e.g., 'Phishing Campaign Jan 2026')"
                  className="w-full px-3 py-2 text-sm bg-bg-tertiary border border-border-default rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-brand-blue"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Processing / Results */}
      {processingState !== 'idle' && (
        <div className="space-y-4">
          {/* Summary stats */}
          {processingState === 'complete' && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-4 p-4 bg-bg-tertiary/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm font-medium text-text-secondary">
                    {successCount} closed in Alexein
                  </span>
                </div>
                {failureCount > 0 && (
                  <div className="flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-500" />
                    <span className="text-sm font-medium text-text-secondary">
                      {failureCount} failed
                    </span>
                  </div>
                )}
              </div>
              {defenderSyncFailedCount > 0 && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800 dark:text-amber-200">
                      <strong>{defenderSyncFailedCount} incident{defenderSyncFailedCount !== 1 ? 's' : ''}</strong> could not be synced to Microsoft Defender.
                      The incidents are closed in Alexein but may still appear open in the customer's Defender portal.
                      You may need to close them manually in Defender.
                    </div>
                  </div>
                </div>
              )}
              {feedbackFailedCount > 0 && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-800 dark:text-amber-200" role="alert">
                  {feedbackFailedCount} incident rating{feedbackFailedCount === 1 ? '' : 's'} could not be saved. The incidents were still closed.
                </div>
              )}
            </div>
          )}

          {/* Results list */}
          <div className="max-h-64 overflow-y-auto space-y-2">
            {processingState === 'processing' ? (
              // Show processing animation
              selectedIncidents.map((inc, idx) => (
                <div
                  key={`${inc.tenantId}-${inc.incidentId}`}
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-lg',
                    idx <= currentIndex
                      ? 'bg-blue-50 dark:bg-blue-900/20'
                      : 'bg-bg-tertiary/50'
                  )}
                >
                  {idx < currentIndex ? (
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  ) : idx === currentIndex ? (
                    <Loader2 className="w-4 h-4 text-accent-blue animate-spin flex-shrink-0" />
                  ) : (
                    <div className="w-4 h-4 rounded-full border-2 border-border-default flex-shrink-0" />
                  )}
                  <span className="text-sm text-text-secondary truncate">
                    {inc.title}
                  </span>
                </div>
              ))
            ) : (
              // Show results
              results.map((result) => (
                <div
                  key={`${result.tenantId}-${result.incidentId}`}
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-lg',
                    !result.success
                      ? 'bg-red-50 dark:bg-red-900/20'
                      : result.defenderSyncFailed
                        ? 'bg-amber-50 dark:bg-amber-900/20'
                        : 'bg-green-50 dark:bg-green-900/20'
                  )}
                >
                  {!result.success ? (
                    <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  ) : result.defenderSyncFailed ? (
                    <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  ) : (
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-text-secondary truncate block">
                      {result.title}
                    </span>
                    {result.error && (
                      <span className="text-xs text-red-600 dark:text-red-400">{result.error}</span>
                    )}
                    {result.success && result.defenderSyncFailed && (
                      <span className="text-xs text-amber-600 dark:text-amber-400">Defender sync failed</span>
                    )}
                    {result.success && result.feedbackFailed && (
                      <span className="text-xs text-amber-600 dark:text-amber-400">AI feedback was not saved</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Classification applied note */}
          {processingState === 'complete' && successCount > 0 && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-700 dark:text-blue-300">
              Applied: <strong>{classification ? getClassificationLabel(classification) : '—'}</strong> /{' '}
              <strong>{determination ? getDeterminationLabel(determination) : '—'}</strong>
              {linkIncidents && linkGroupName && (
                <>
                  {' '}
                  • Linked as: <strong>{linkGroupName}</strong>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
